import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DateDrillDownComponent } from "@components/date-drill-down/date-drill-down.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { IDateDrillDownSelectedItem } from "~app/common/components/date-drill-down/components/date-drill-down-selector/models/dateDrillDownSelectedItem";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { AggregatPeriods, IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { ForsorjningskallaAggregat } from "~app/common/models/forsorjningskalla";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { FordelaService } from "~app/common/services/fordela.service";
import { SetLeveransradAvtaladKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/SetAvtaladKvantitetCommand";
import { SetForsorjningsradAvtaladCommandFactory } from "~app/features/justera-leveransplan/common/commands/SetForsorjningsradAvtaladCommands";
import { SetKundorderradLeveransradKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/SetKundorderradLeveransradKvantitetCommand";
import { SetLeveransradKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/SetLeveransradKvantitetCommand";
import { SetForsorjningsradPlanCommandFactory } from "~app/features/justera-leveransplan/common/commands/setForsorjningsradPlanCommands";
import { OverenskommentKvantitetCommandFactory } from "~app/features/justera-leveransplan/common/commands/setOverenskommenKvantitetCommands";
import { SetLeveransradKvantitetEnqueued } from "../../../common/events";
import { IKundorderDetailKundorder, IKundorderDetailKundorderrad } from "../../../models/kundorder-detail-model";
import { KorForsorjningsradCreateComponent } from "../kor-forsorjningsrad-create/kor-forsorjningsrad-create.component";
import { KorForsorjningsradEditComponent } from "../kor-forsorjningsrad-edit/kor-forsorjningsrad-edit.component";
import { ForsorjningskallaCellComponent } from "./forsorjningskalla-cell/forsorjningskalla-cell.component";
import { BalansHeaderCell, SettingsCell } from "./kor-hot.cells";
import { ITaktSettings, KundorderradAggregat } from "./kor-hot.model";

@Component({
    selector: "rask-kor-hot",
    templateUrl: "./kor-hot.component.html",
    styleUrls: ["./kor-hot.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateDrillDownComponent,
        ActionButtonComponent,
        CheckboxComponent,
        HotDocComponent,
        TranslatePipe
    ]
})

/* eslint-disable no-bitwise */

export class KorHotComponent implements OnInit, OnDestroy {
    aggregatePeriods: IAggregatPeriod[];
    @Input() canEdit: boolean;
    currentPeriod: IAggregatPeriod;
    hotDoc: HotDoc;
    @Input() kundorder: IKundorderDetailKundorder;
    @Input() kundorderrad: IKundorderDetailKundorderrad;
    model: KundorderradAggregat;
    showAvtaladVolym = false;
    taktSettings: ITaktSettings;

    private readonly $translate = inject(TranslateService);
    private readonly commandService = inject(CommandService);
    private readonly eventService = inject(EventService);
    private readonly fordelaKvantitetService = inject(FordelaService);
    private readonly modalService = inject(NgbModal);
    private readonly ngUnsubscribe$ = new Subject<void>();

    addForsorjningskalla = () => {
        const modalRef = this.modalService.open(KorForsorjningsradCreateComponent, {
            keyboard: false,
            centered: true,
            backdrop: "static",
            size: "lg"
        });
        modalRef.componentInstance.kundorderrad = this.model;

        this.onModalResult(modalRef);
    };

    createHotDoc = () => {
        const hotDocSettings: IHotDocSettings = {
            raskSettings: {
                calculateSum: true
            }
        };

        const colFactory = () => this.buildColumnDefinitions();
        const rowFactory = (doc: HotDoc) => {
            const periods = this.aggregatePeriods;
            const model = this.model;

            if (this.showAvtaladVolym) {
                doc.createRow(row => this.buildKundorderradAvtaladrad(row, model, periods));
            }
            doc.createRow(row => this.buildKundorderradLeveransrad(row, model, periods));
            doc.createRow(row => this.buildKundorderradTransporteratrad(row, model, periods));
            doc.createRow(row => this.buildKundorderradBalansrad(row, model, periods));
            doc.createRow(row => this.buildKundorderradForsorjningsraderRad(row, model, periods));

            model.forsorjningskallor.forEach(forsorjningskalla => {
                if (this.showAvtaladVolym) {
                    doc.createRow(row => this.buildForsorjningsradAvtaladrad(row, forsorjningskalla, model, periods), Cellstyle.Default);
                }
                doc.createRow(row => this.buildForsorjningsradPlanrad(row, forsorjningskalla, model, periods), Cellstyle.Default);
                doc.createRow(row => this.buildForsorjningsradTransporteratrad(row, forsorjningskalla, periods), Cellstyle.Default);
            });
        };

        return new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    };

    dagarKvarInnevarandeManad(settings: ITaktSettings): number {
        return settings.totaltAntalArbetsdagarInnevarandeManad - Math.round(settings.passeradeAntalArbetsdagarInnevarandeManad);
    }

    initPeriods = (): void => {
        const defaultPeriod = {
            type: "PERIOD",
            from: this.kundorderrad.periodFrom,
            to: this.kundorderrad.periodTom,
            periodMonth: this.kundorderrad.periodFrom
        };
        this.aggregatePeriods = AggregatPeriods.create("sv-SE", defaultPeriod);
        this.currentPeriod = this.aggregatePeriods.find(a => a.isCurrentlyActive) || null;

    };

    initTaktSettings = (): void => {
        this.taktSettings = {
            totaltAntalArbetsdagarInnevarandeManad: this.kundorderrad.totaltAntalArbetsdagarInnevarandeManad,
            passeradeAntalArbetsdagarInnevarandeManad: this.kundorderrad.passeradeAntalArbetsdagarInnevarandeManad,
            showTakt: true
        };
    };

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.initTaktSettings();
        this.initPeriods();
        this.model = new KundorderradAggregat(this.kundorder, this.kundorderrad);
        this.hotDoc = this.createHotDoc();

        this.eventService.subscribeEvent<SetLeveransradKvantitetEnqueued>(SetLeveransradKvantitetEnqueued.type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => event.enqueued.kundorderradId === this.kundorderrad.id))
            .subscribe(event => {
                this.model.updateLeveransradKvantitet(event.enqueued.fordeladKvantitet);
                this.hotDoc.rebind();
            });
    }

    onDateSelected(item: IDateDrillDownSelectedItem) {
        const selectedPeriod = {
            type: item.periodType,
            from: item.periodStart,
            to: item.periodEnd,
            periodMonth: item.periodMonth
        };

        this.aggregatePeriods = AggregatPeriods.create("sv-SE", selectedPeriod);
        this.hotDoc.rebind();
    }

    toggleAvtaladVolym = () => {
        // Bind laddar enbart om kolumner, rebind fixar rader också
        this.hotDoc.rebind();
    };

    toggleTakt = () => {
        this.hotDoc.bind();
    };

    private readonly buildColumnDefinitions = (): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];

        columns.push({
            header: "",
            width: 30,
            readOnly: true,
            cellName: "settings"
        });

        columns.push({
            header: "",
            width: 220,
            readOnly: true,
            cellName: "rowHeader1"
        });

        columns.push({
            header: "",
            width: 110,
            readOnly: true,
            cellName: "rowHeader2"
        });

        if (this.taktSettings.showTakt) {
            columns.push({
                header: "Takt",
                width: 60,
                readOnly: true,
                cellName: "takt",
                type: "numeric"
            });
        }

        const periodCols = this.aggregatePeriods.map(period => ({
            header: period.name,
            width: 95,
            cellName: period.key,
            type: "numeric"
        }));
        columns.push(...periodCols);

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
            cellName: "summa",
            type: "numeric",
            width: 95
        });
        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT_LEVERANSRAD"),
            cellName: "transporteratleveransrad",
            type: "numeric",
            width: 140
        });
        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_OVERENSKOMMET"),
            cellName: "overenskommet",
            type: "numeric",
            width: 130
        });
        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_AVVIKELSE"),
            cellName: "avvikelse",
            type: "numeric",
            width: 95
        });

        return columns;
    };

    private readonly onModalResult = (modalRef: NgbModalRef) => {
        modalRef.result.then(result => {
            if (result) {
                this.hotDoc.rebind();
            }
        }, () => {
            // Fånga error
        });
    };

    private readonly onModifyForsorjningsrad = (kundorderrad: KundorderradAggregat, model: ForsorjningskallaAggregat) => {
        const modalRef = this.modalService.open(KorForsorjningsradEditComponent, {
            keyboard: false,
            centered: true,
            backdrop: "static",
            size: "lg"
        });
        modalRef.componentInstance.forsorjningskalla = model;
        modalRef.componentInstance.kundorderrad = kundorderrad;

        this.onModalResult(modalRef);
    };

    private readonly onUpdateForsorjningsradAvtalad = (
        model: ForsorjningskallaAggregat,
        kundorderrad: KundorderradAggregat,
        period: IAggregatPeriod,
        change: NumericCellChangeArg) => {
        const periodStart = period.getStartOrLater(kundorderrad.periodFrom);
        const periodEnd = period.getEndOrEarlier(kundorderrad.periodTom);

        this.fordelaKvantitetService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateForsorjningsradAvtalad(result);

                const command = SetForsorjningsradAvtaladCommandFactory.createSetForsorjningsradAvtaladCommand(model, kundorderrad.kundorderradId, periodStart, periodEnd, result);
                this.commandService.addTransactionCommand(command);

                change.complete();
            });

    };

    private readonly buildForsorjningsradAvtaladrad = (
        row: HotRow,
        forsorjningsAggregat: ForsorjningskallaAggregat,
        kundorderAggregat: KundorderradAggregat,
        periods: IAggregatPeriod[]): void => {
        if (this.canEdit) {
            const settingsCell = new SettingsCell(
                Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
                { rowSpan: 3 },
                () => this.onModifyForsorjningsrad(kundorderAggregat, forsorjningsAggregat));
            row.add("settings", settingsCell);
        }
        row.addAngularComponent("rowHeader1", ForsorjningskallaCellComponent, (instance) => {
            instance.applyData(forsorjningsAggregat, kundorderAggregat, this.canEdit);
        }, Cellstyle.Readonly, { rowSpan: 3 });
        row.addText("rowHeader2", "Avtalad", Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        let totaltAvtaladAllaPerioder = 0;
        periods.forEach(period => {
            const avtaladForPeriod = forsorjningsAggregat.sumAvtalat(period);
            totaltAvtaladAllaPerioder += avtaladForPeriod;

            const isActive = period.isWithin(kundorderAggregat.periodFrom, kundorderAggregat.periodTom);
            const style = isActive ? 0 : Cellstyle.Readonly;
            row.addNumeric(period.key, avtaladForPeriod, style, change => this.onUpdateForsorjningsradAvtalad(forsorjningsAggregat, kundorderAggregat, period, change));
        });

        row.addNumeric("summa", totaltAvtaladAllaPerioder, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("transporteratleveransrad", "", Cellstyle.Readonly);
        row.addText("overenskommet", "", Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly onUpdateForsorjningsradPlan = (
        model: ForsorjningskallaAggregat,
        kundorderrad: KundorderradAggregat,
        period: IAggregatPeriod,
        change: NumericCellChangeArg) => {
        const periodStart = period.getStartOrLater(kundorderrad.periodFrom);
        const periodEnd = period.getEndOrEarlier(kundorderrad.periodTom);

        this.fordelaKvantitetService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateForsorjningsradPlan(result);

                const command = SetForsorjningsradPlanCommandFactory.createSetForsorjningsradPlanCommand(model, kundorderrad.kundorderradId, periodStart, periodEnd, result);
                this.commandService.addTransactionCommand(command);

                change.complete();
            });

    };

    private readonly onUpdateOverenskommenKvantitet = (model: ForsorjningskallaAggregat, kundorderrad: KundorderradAggregat, change: NumericCellChangeArg) => {
        model.overenskommet = change.newValue;

        const command = OverenskommentKvantitetCommandFactory.createSetOverenskommentKvantitetCommand(model, kundorderrad.kundorderradId, change.newValue);
        this.commandService.addTransactionCommand(command);

        change.complete();
    };

    private readonly buildForsorjningsradPlanrad = (row: HotRow, model: ForsorjningskallaAggregat, kundorderrad: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        if (this.canEdit && !this.showAvtaladVolym) {
            const settingsCell = new SettingsCell(
                Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
                { rowSpan: 2 },
                () => this.onModifyForsorjningsrad(kundorderrad, model));
            row.add("settings", settingsCell);
        }
        if (!this.showAvtaladVolym) {
            row.addAngularComponent("rowHeader1", ForsorjningskallaCellComponent, (instance) => {
                instance.applyData(model, kundorderrad, this.canEdit);
            }, Cellstyle.Readonly, { rowSpan: 2 });
        }

        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_PLAN"), Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt",
            model.calculateTakt(this.currentPeriod,
                this.taktSettings.passeradeAntalArbetsdagarInnevarandeManad,
                this.taktSettings.totaltAntalArbetsdagarInnevarandeManad),
            Cellstyle.Readonly,
            percentSettings);

        periods.forEach(period => {
            const total = model.sumPlan(period);
            const isActive = period.isWithin(kundorderrad.periodFrom, kundorderrad.periodTom);
            const style = isActive ? 0 : Cellstyle.Readonly;
            row.addNumeric(period.key, total, style, change => this.onUpdateForsorjningsradPlan(model, kundorderrad, period, change));
        });

        row.addText("summa", "", Cellstyle.Readonly);
        row.addNumeric("transporteratleveransrad", model.sumTransporteratPlanerat(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.overenskommet, null, change => this.onUpdateOverenskommenKvantitet(model, kundorderrad, change));
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildForsorjningsradTransporteratrad = (row: HotRow, model: ForsorjningskallaAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("settings", Cellstyle.Default);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT"), Cellstyle.Readonly);

        let grandtotal = 0;
        periods.forEach(period => {
            const total = model.sumTransporterat(period);
            grandtotal += total;
            row.addNumeric(period.key, total, Cellstyle.Readonly);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("transporteratleveransrad", "", Cellstyle.Readonly);
        row.addText("overenskommet", "", Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly onUpdateAvtaladKvantitetMain = (change: NumericCellChangeArg, model: KundorderradAggregat, period: IAggregatPeriod): void => {
        const periodStart = period.getStartOrLater(model.periodFrom);
        const periodEnd = period.getEndOrEarlier(model.periodTom);

        this.fordelaKvantitetService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateAvtaladKvantitet(result);

                this.commandService.addTransactionCommand(new SetLeveransradAvtaladKvantitetCommand(c => {
                    c.kundorderradId = model.kundorderradId;
                    c.periodStart = periodStart;
                    c.periodEnd = periodEnd;
                    c.avtaladKvantitet = result;
                }));

                change.complete();
            });
    };

    private readonly buildKundorderradAvtaladrad = (row: HotRow, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("settings", Cellstyle.Readonly); // Behövs för rad-style
        row.addText("rowHeader1", "Avtalad", Cellstyle.Readonly | Cellstyle.AlignRight, { colSpan: 2 });

        let totaltAvtaladAllaPerioder = 0;
        periods.forEach(period => {
            const avtaladForPeriod = model.sumAvtalat(period);
            totaltAvtaladAllaPerioder += avtaladForPeriod;

            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? Cellstyle.Default : Cellstyle.Readonly;
            row.addNumeric(period.key, avtaladForPeriod, style, arg => this.onUpdateAvtaladKvantitetMain(arg, model, period));
        });

        row.addNumeric("summa", totaltAvtaladAllaPerioder, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildKundorderradBalansrad = (row: HotRow, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.add("rowHeader1", new BalansHeaderCell(this.$translate));

        let grandtotal = 0;
        periods.forEach(period => {
            const balans = model.sumBalans(period);
            grandtotal += balans;

            const style = Cellstyle.Readonly | (period.isCurrentlyActive ? Cellstyle.ActivePeriod : 0);
            row.addNumeric(period.key, balans, style);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("transporteratleveransrad", "", Cellstyle.Readonly);
        row.addText("overenskommet", null, Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly buildKundorderradForsorjningsraderRad = (row: HotRow, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        const header = this.$translate.instant("KUNDORDER_LEVRADER_HEADER_FORSORJNINGSGRADER");
        const headerStyle = Cellstyle.Readonly | Cellstyle.AlignRight;
        row.addText("rowHeader1", header, headerStyle, { colSpan: 2 });

        periods.forEach(period => {
            const sumForsorjningsrader = model.sumForsorjningsrader(period);
            row.addNumeric(period.key, sumForsorjningsrader, Cellstyle.Readonly);
        });
    };

    private readonly onUpdateKundorderradKvantitet = (change: NumericCellChangeArg, model: KundorderradAggregat) => {
        model.overenskommet = change.newValue;

        this.commandService.addTransactionCommand(new SetKundorderradLeveransradKvantitetCommand(c => {
            c.kundorderradId = model.kundorderradId;
            c.overenskommenKvantitetLeveransrad = change.newValue;
        }));

        change.complete();
    };

    private readonly onUpdateLeveransradKvantitet = (change: NumericCellChangeArg, model: KundorderradAggregat, period: IAggregatPeriod) => {
        const periodStart = period.getStartOrLater(model.periodFrom);
        const periodEnd = period.getEndOrEarlier(model.periodTom);

        this.fordelaKvantitetService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateLeveransradKvantitet(result);

                this.commandService.addTransactionCommand(new SetLeveransradKvantitetCommand(c => {
                    c.kundorderradId = model.kundorderradId;
                    c.periodStart = periodStart;
                    c.periodEnd = periodEnd;
                    c.fordeladKvantitet = result;
                }));

                change.complete();
            });
    };

    private readonly buildKundorderradLeveransrad = (row: HotRow, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("settings", Cellstyle.Readonly); // Behövs för rad-style
        const header = this.$translate.instant("KUNDORDER_LEVRADER_HEADER_LEVERANSRAD");
        const headerStyle = Cellstyle.Readonly | Cellstyle.AlignRight;
        row.addText("rowHeader1", header, headerStyle, { colSpan: 2 });

        const percentSettings = { noZero: true };
        row.addPercent("takt", model.calculateTaktLeverans(this.currentPeriod,
            this.taktSettings.passeradeAntalArbetsdagarInnevarandeManad,
            this.taktSettings.totaltAntalArbetsdagarInnevarandeManad),
        Cellstyle.Readonly,
        percentSettings);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumLeveransrad = model.sumLeveransrad(period);
            grandtotal += sumLeveransrad;

            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? Cellstyle.Default : Cellstyle.Readonly;
            row.addNumeric(period.key, sumLeveransrad, style, arg => this.onUpdateLeveransradKvantitet(arg, model, period));
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addNumeric("transporteratleveransrad", model.sumTransporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.overenskommet, Cellstyle.Default, change => this.onUpdateKundorderradKvantitet(change, model));
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildKundorderradTransporteratrad = (row: HotRow, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        const header = this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT");
        const headerStyle = Cellstyle.Readonly | Cellstyle.AlignRight;
        row.addText("rowHeader1", header, headerStyle, { colSpan: 2 });

        let grandtotal = 0;
        periods.forEach(period => {
            const transporterat = model.sumTransporterat(period);
            grandtotal += transporterat;

            // Jämför mot kundorderradens aggregat för aktuella försörjningskällor
            const aktuellaForsorjningskallor = model.forsorjningskallor.map(kalla => kalla.id);
            const transporteratForsorjningskallor = model.sumTransporteratForsorjningskallor(period, aktuellaForsorjningskallor);

            const flash = transporteratForsorjningskallor !== transporterat ? Cellstyle.Flash : Cellstyle.Default;
            row.addNumeric(period.key, transporterat, Cellstyle.Readonly | flash);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };
}
